/**
 * Build a compact human summary of mirror↔DB drift for Align-button hover.
 * Uses parity samples (keys + statuses) already returned by the mapping API.
 */

function sampleKey(entry) {
  if (entry == null) return ''
  if (typeof entry === 'string') return entry
  return String(entry.key ?? '')
}

function sampleStatus(entry) {
  if (entry == null || typeof entry === 'string') return ''
  return String(entry.status ?? entry.sheet_status ?? '').trim()
}

function formatKeyedStatus(entry) {
  const key = sampleKey(entry)
  if (!key) return null
  const st = sampleStatus(entry)
  return st ? `${key} (${st})` : key
}

function formatMismatch(entry) {
  const key = sampleKey(entry)
  if (!key) return null
  const mirror =
    String(
      entry?.sheet_status ?? entry?.sheet_partial ?? entry?.sheet_value ?? '',
    ).trim() || 'blank'
  const db =
    String(entry?.db_status ?? entry?.db_partial ?? entry?.db_value ?? '').trim() ||
    'blank'
  return `${key}: mirror=${mirror} · db=${db}`
}

function formatList(items, limit = 8) {
  const list = (items || []).map((x) => x).filter(Boolean)
  if (!list.length) return '—'
  const shown = list.slice(0, limit)
  const extra = list.length > limit ? ` (+${list.length - limit} more in sample)` : ''
  return shown.join('\n') + extra
}

/**
 * @param {object|null|undefined} parity
 * @returns {{ plain: string, lines: { heading: string, body: string }[] }}
 */
export function buildParityHoverSummary(parity) {
  if (!parity) {
    return {
      plain: 'Upsert mirror rows into Postgres',
      lines: [
        {
          heading: 'Align',
          body: 'Upsert mirror rows into Postgres. Load data for drift preview.',
        },
      ],
    }
  }

  if (parity.aligned == null) {
    const msg =
      parity.message ||
      'Parity check disabled — Postgres is source of truth for this mapping table.'
    return {
      plain: msg,
      lines: [{ heading: 'Postgres', body: msg }],
    }
  }

  if (parity.aligned) {
    return {
      plain: 'In sync — Align still upserts into Postgres',
      lines: [
        {
          heading: 'In sync',
          body: 'No mirror-only / DB-only / status drift in the current filter. Align still upserts into Postgres.',
        },
      ],
    }
  }

  const sheetOnly = (parity.sheet_only_sample || [])
    .map(formatKeyedStatus)
    .filter(Boolean)
  const dbOnly = (parity.db_only_sample || []).map(formatKeyedStatus).filter(Boolean)
  const mismatches = (parity.status_mismatch_sample || [])
    .map(formatMismatch)
    .filter(Boolean)

  const sheetCount = parity.sheet_only_count ?? sheetOnly.length
  const dbCount = parity.db_only_count ?? dbOnly.length
  const mismatchCount = parity.status_mismatch_count ?? mismatches.length

  const lines = []
  if (sheetCount > 0) {
    lines.push({
      heading: `Mirror-only (${sheetCount})`,
      body: formatList(sheetOnly),
    })
  }
  if (dbCount > 0) {
    lines.push({
      heading: `DB-only (${dbCount}) — Align does not delete these`,
      body: formatList(dbOnly),
    })
  }
  if (mismatchCount > 0) {
    const mismatchHeading = parity.mismatch_label || 'Status mismatch'
    lines.push({
      heading: `${mismatchHeading} (${mismatchCount})`,
      body: formatList(mismatches),
    })
  }
  if (!lines.length) {
    lines.push({
      heading: 'Drift detected',
      body: parity.message || 'Mirror and Postgres differ.',
    })
  }

  const plainParts = [
    sheetCount ? `Mirror-only ${sheetCount}: ${sheetOnly.slice(0, 5).join('; ')}` : null,
    dbCount ? `DB-only ${dbCount}: ${dbOnly.slice(0, 5).join('; ')}` : null,
    mismatchCount
      ? `${parity.mismatch_label || 'Status mismatch'} ${mismatchCount}: ${mismatches.slice(0, 3).join('; ')}`
      : null,
  ].filter(Boolean)

  return {
    plain: plainParts.join(' · ') || parity.message || 'Drift detected',
    lines,
  }
}
